import React, { useEffect, useState, lazy, Suspense } from 'react';
import axios from 'axios';
import FlightSearchForm from './FlightSearchForm';
import { useAirports, useAirlines } from '../data/geoData';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle, Globe, ArrowRight, Globe2, Info, MessageCircle, X, CloudSun, MapPin } from 'lucide-react';
import PopularDestinations from '../components/PopularDestinations';
import Temoignages from '../components/Temoignages';
import AutresDestinations from '../components/AutresDestinations';
import Footer from '../components/Footer';
import PassengerForm from './PassengerForm';
import ReservationSheet from './ReservationSheet';
import MapAeroports from '../components/MapAeroports';
import NewsList from '../components/NewsList';
import CountryInfo from '../components/CountryInfo';
import { groupPriceBreakdown } from '../utils/priceAllocation';
import { computeAdjustedPrice, getFlightCountries } from '../utils/flightPricing';
import { useAuth } from '../account/AuthContext';
// Assistant IA chargé en lazy (uniquement quand le chat est ouvert) :
// son propre module charge airports.json → on l'isole du bundle initial.
const AITravelAssistant = lazy(() => import('../ia/AITravelAssistant'));

// Codes IATA de compagnies aériennes à ignorer/rejeter dans les résultats de
// vol (ex: "ZZ"). Un vol dont la compagnie (marketing OU operating) porte un
// de ces codes n'est ni affiché ni retenu pour la réservation.
const BANNED_AIRLINE_CODES = ['ZZ'];

// Associe chaque entrée de franchise bagages d'un vol à son trajet (aller / retour / étape)
// et à son numéro de segment, afin d'afficher un détail bagages clair par direction.
const getBaggageByItinerary = (flight) => {
  const itineraries = flight.itineraries || [];
  const fareDetails = flight.travelerPricings?.[0]?.fareDetailsBySegment || [];
  const counts = itineraries.map((it) => (it.segments || []).length);
  const totalItineraries = itineraries.length;
  const isMultiCity = flight.tripType === "multiCity";
  const result = [];
  let global = 0;
  counts.forEach((count, itIdx) => {
    let tag = isMultiCity ? `etape ${itIdx + 1}` : "aller";
    if (!isMultiCity && totalItineraries === 2) tag = itIdx === 0 ? "aller" : "retour";
    else if (totalItineraries > 2) tag = itIdx === 0 ? "aller" : (itIdx === totalItineraries - 1 ? "retour" : `étape ${itIdx + 1}`);
    if (isMultiCity) tag = `etape ${itIdx + 1}`;
    for (let s = 0; s < count; s++) {
      result.push({ itinerary: tag, segment: s + 1, fare: fareDetails[global] || null });
      global++;
    }
  });
  while (global < fareDetails.length) {
    result.push({ itinerary: isMultiCity ? "etape" : (itineraries.length === 2 ? "retour" : "aller"), segment: global + 1, fare: fareDetails[global] });
    global++;
  }
  return result;
};

const formatDuffelCondition = (condition) => {
  if (!condition) return "Non précisé";
  if (condition.allowed === false) return "Non";
  if (condition.allowed === true) return "Oui";
  if (condition.penalty_amount && condition.penalty_currency) {
    return `Oui, pénalité ${condition.penalty_amount} ${condition.penalty_currency}`;
  }
  return "Selon conditions du tarif";
};

// Libellé français d'un code de direction (aller / retour / étape N)
const getItineraryLabel = (tag) => {
  if (tag === "retour") return "Retour";
  if (tag === "aller") return "Aller";
  return "Étape";
};

function Home() {
  // Données aéroports / compagnies chargées en lazy (chunks séparés)
  const airports = useAirports();
  const airlines = useAirlines();

  // Session client (espace client)
  const { customer } = useAuth();

  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [reservationData, setReservationData] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [travelInfo, setTravelInfo] = useState(null);
  const [news, setNews] = useState([]);
  const [showPassengerForm, setShowPassengerForm] = useState(false);


  const [showChat, setShowChat] = useState(false);
  const [voiceChatRequested, setVoiceChatRequested] = useState(false);



  // --- Charger infos pays + news quand un vol est sélectionné ---
  useEffect(() => {
    if (selectedFlight && !showPassengerForm) {
      const lastItinerary = selectedFlight.itineraries[selectedFlight.itineraries.length - 1];
      const arrival = lastItinerary.segments[lastItinerary.segments.length - 1];
      const airport = getAirportData(arrival.arrival.iataCode);

      if (airport?.iso_country) {
        getTravelInfo(airport.iso_country);
        getCountryNews(airport.country);
      }

      const timer = setTimeout(() => {
        if (!travelInfo && news.length === 0) {
          setShowPassengerForm(true);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlight, showPassengerForm, travelInfo, news.length]);





  const aircraftTypes = {
    "318": "Airbus A318", "319": "Airbus A319", "320": "Airbus A320", "321": "Airbus A321", "32N": "Airbus A320neo", "32Q": "Airbus A321neo",
    "330": "Airbus A330", "332": "Airbus A330-200", "333": "Airbus A330-300", "338": "Airbus A330-800neo", "339": "Airbus A330-900neo",
    "342": "Airbus A340-200", "343": "Airbus A340-300", "345": "Airbus A340-500", "346": "Airbus A340-600", "350": "Airbus A350",
    "351": "Airbus A350-900", "359": "Airbus A350-900", "35K": "Airbus A350-1000", "388": "Airbus A380-800",
    "717": "Boeing 717", "727": "Boeing 727", "732": "Boeing 737-200", "733": "Boeing 737-300", "734": "Boeing 737-400",
    "735": "Boeing 737-500", "736": "Boeing 737-600", "737": "Boeing 737-700", "738": "Boeing 737-800", "739": "Boeing 737-900",
    "73H": "Boeing 737-800 (winglets)", "73J": "Boeing 737 MAX 9", "73M": "Boeing 737 MAX 8", "752": "Boeing 757-200",
    "753": "Boeing 757-300", "762": "Boeing 767-200", "763": "Boeing 767-300", "764": "Boeing 767-400ER", "772": "Boeing 777-200",
    "773": "Boeing 777-300", "77L": "Boeing 777-200LR", "77W": "Boeing 777-300ER", "778": "Boeing 777-8", "779": "Boeing 777-9",
    "787": "Boeing 787 Dreamliner", "788": "Boeing 787-8 Dreamliner", "789": "Boeing 787-9 Dreamliner", "78X": "Boeing 787-10 Dreamliner",
    "E70": "Embraer 170", "E75": "Embraer 175", "E90": "Embraer 190", "E95": "Embraer 195", "E2S": "Embraer E190-E2", "E2L": "Embraer E195-E2",
    "CRJ": "Bombardier CRJ", "CR1": "Bombardier CRJ100", "CR2": "Bombardier CRJ200", "CR7": "Bombardier CRJ700", "CR9": "Bombardier CRJ900",
    "CRA": "Bombardier CRJ1000", "MRJ": "Mitsubishi SpaceJet", "AT4": "ATR 42", "AT5": "ATR 42-500", "AT7": "ATR 72", "ATR": "ATR 72-600",
    "F70": "Fokker 70", "F100": "Fokker 100", "DC3": "Douglas DC-3", "DC9": "Douglas DC-9", "MD8": "McDonnell Douglas MD-80",
    "MD9": "McDonnell Douglas MD-90", "MD1": "McDonnell Douglas MD-11",
  };

  const getIcaoFromIata = (iata) => {
    if (!airlines?.data) return null;
    const found = airlines.data.find(a => a.iata_code === iata);
    return found?.icao_code || null;
  };

  const getCompagnyName = (iata) => {
    if (!airlines?.data) return null;
    const found = airlines.data.find(a => a.iata_code === iata);
    return found?.name || null;
  };




  // Infos administratives via proxy backend (évite CORS)
  const getTravelInfo = async (countryCode) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'country',
            param: countryCode
          }
        }
      );
      if (res.data && res.data[0]) {
        setTravelInfo(res.data[0]);
      }
    } catch (e) {
      console.error("Erreur travel info:", e);
    }
  };





  const getCountryNews = async (countryCode) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'news',
            param: countryCode
          }
        }
      );
      setNews(res.data.articles || []);
    } catch (e) {
      console.error("Erreur news:", e);
    }
  };




  // --- Fonction météo via proxy backend ---
  const getWeatherForecast = async (lat, lon, datetime) => {
    try {
      const date = new Date(datetime);
      const isoDate = date.toISOString().split("T")[0];
      const hour = date.getHours();

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/external_apis/proxy.php`,
        {
          params: {
            type: 'weather',
            lat: lat,
            lon: lon,
            date: isoDate
          }
        }
      );

      if (res.data?.hourly) {
        const index = res.data.hourly.time.findIndex((t) => t.includes(`T${String(hour).padStart(2, "0")}:00`));
        if (index !== -1) {
          return {
            temperature: res.data.hourly.temperature_2m[index],
            code: res.data.hourly.weathercode[index],
          };
        }
      }
    } catch (e) {
      console.error("Erreur météo :", e);
    }
    return null;
  };

  // --- Mapping simple Open-Meteo weather codes ---
  const weatherIcons = {
    0: "☀️", 1: "🌤️", 2: "⛅", 3: "☁️",
    45: "🌫️", 51: "🌦️", 61: "🌧️", 71: "❄️", 95: "⛈️",
  };





  function getItineraryType(flight) {
    const { depCountry, arrCountry } = getFlightCountries(flight);
    if (!depCountry || !arrCountry) return "inconnu";
    if (depCountry === "CI" && arrCountry === "CI") return "national";
    return "international";
  }








  const getAdjustedPrice = (flight) => {
    const totalPassengers = (flight.travelerPricings?.length) || 1;
    return computeAdjustedPrice(flight, totalPassengers);
  };

  const getTravelerTypeLabel = (type) => {
    switch (type) {
      case "ADULT":
        return "Adulte";
      case "CHILD":
        return "Enfant";
      case "HELD_INFANT":
        return "Bebe";
      default:
        return type || "Passager";
    }
  };

  const getTravelerPriceBreakdown = (flight) => {
    const currency = flight.price?.currency || "XOF";
    const types = (flight.travelerPricings || []).map((tp) => tp.travelerType || "ADULT");
    if (!types.length) return [];
    const total = getAdjustedPrice(flight);
    return groupPriceBreakdown(total, types).map((g) => ({ ...g, currency }));
  };

  const getOfferSlices = (flight) => flight?.slices || flight?.duffel_slices || [];

  const getOfferSegments = (flight) => {
    const slices = getOfferSlices(flight);
    return slices.flatMap((slice) => slice?.segments || []);
  };

  const getFirstFareDetail = (flight) => {
    const travelerPricings = flight?.travelerPricings || [];
    for (const traveler of travelerPricings) {
      const segments = traveler?.fareDetailsBySegment || [];
      if (segments.length) return segments[0];
    }

    const firstPassenger = getOfferSegments(flight)?.[0]?.passengers?.[0];
    if (firstPassenger) {
      return {
        cabin: firstPassenger.cabin || null,
        fareBasis: firstPassenger.fare_basis_code || null,
      };
    }

    return null;
  };

  const getCabinDetails = (flight) => {
    const firstPassenger = getOfferSegments(flight)?.[0]?.passengers?.[0];
    if (firstPassenger?.cabin) return firstPassenger.cabin;

    const fareDetail = getFirstFareDetail(flight);
    return fareDetail?.cabin || flight?.cabin || {};
  };

  const getFlightAircraft = (flight) => {
    const firstSegment = getOfferSegments(flight)?.[0];
    return firstSegment?.aircraft || flight?.aircraft || null;
  };

  const enrichFlightWithDuffelOffer = async (flight) => {
    if (!flight || flight.provider !== 'duffel' || !flight.duffel_offer_id) return flight;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/backend_duffel/getOffer.php`,
        { params: { offer_id: flight.duffel_offer_id } }
      );

      const detailedOffer = res.data?.data;
      if (!detailedOffer) return flight;

      const travelerPricings = (detailedOffer.traveler_pricings || detailedOffer.travelerPricings || []).map((tp) => ({
        ...tp,
        fareDetailsBySegment: tp.fare_details_by_segment || tp.fareDetailsBySegment || [],
      }));

      return {
        ...flight,
        ...detailedOffer,
        travelerPricings: travelerPricings.length ? travelerPricings : flight.travelerPricings,
        _duffel_details_loaded: true,
      };
    } catch (error) {
      console.warn('Impossible de recharger l’offre Duffel détaillée:', error);
      return flight;
    }
  };

  const getDuffelBaggageDetails = (flight) => {
    if (!flight || flight.provider !== 'duffel') return [];

    return getBaggageByItinerary(flight).map(({ itinerary, segment, fare }) => {
      const entries = fare?.duffel_baggages?.flatMap((item) => item.baggages || []) || [];
      const included = fare?.includedCheckedBags;
      const computedQuantity = entries.reduce((sum, bag) => sum + (Number(bag.quantity) || 0), 0);
      const quantity = included?.quantity ?? (computedQuantity || 0);
      const weightEntry = entries.find((bag) => bag.weight && (bag.weight_unit || bag.weightUnit));
      const weight = weightEntry ? `${weightEntry.weight} ${weightEntry.weight_unit || weightEntry.weightUnit}` : null;

      return {
        itinerary,
        segment,
        label: `${getItineraryLabel(itinerary)}${itinerary.indexOf('étape') === 0 ? ' #' + itinerary.split(' ')[1] : ''}`,
        quantity,
        weight,
        details: entries.length ? entries.map((bag) => {
          const bagType = {
            checked: 'Bagage en soute',
            carry_on: 'Bagage cabine',
            personal_item: 'Effet personnel',
          }[bag.type] || bag.type || 'Bagage';
          const bagWeight = bag.weight && (bag.weight_unit || bag.weightUnit)
            ? `${bag.weight} ${bag.weight_unit || bag.weightUnit}`
            : null;
          return `${bag.quantity ?? 1} ${bagType}${bagWeight ? ` • ${bagWeight}` : ''}`;
        }) : ['Aucune information détaillée fournie'],
      };
    });
  };

  const handleVoiceChat = () => {
    setShowChat(true);
    setVoiceChatRequested(true);
  };

  useEffect(() => {
    const handleOpenVoiceAssistant = () => {
      handleVoiceChat();
    };

    window.addEventListener('open-voice-assistant', handleOpenVoiceAssistant);
    return () => window.removeEventListener('open-voice-assistant', handleOpenVoiceAssistant);
  }, []);

  const handleSearch = async (searchParams) => {
    setLoading(true);
    setErreur(null);
    setFlights([]);

    // Choix du fournisseur de recherche de vols.
    // - 'serpapi'   : backend SerpAPI / Google Flights (actif par défaut)
    // - 'amadeus'   : backend Amadeus (conservé, disponible)
    // - 'travelport': à venir (lorsque le backend sera ajouté)
    // Default actuel : Duffel.
    const flightProvider = 'duffel';
    const flightBackends = {
      amadeus: 'backend_amadeus',
      duffel: 'backend_duffel',
      serpapi: 'backend_serpapi',
      travelport: 'backend_travelport', // à venir
    };
    const backendPath = flightBackends[flightProvider] || 'backend_duffel';

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/${backendPath}/searchFlights.php`,
        {
          params: {
            ...searchParams,
            // Recherche approfondie + vols masqués pour maximiser les résultats.
            // (deep_search consomme plus de crédits SerpAPI ; à désactiver si besoin)
            ...(flightProvider === 'serpapi' ? {
              deep_search: searchParams.deep_search ?? '1',
              show_hidden: searchParams.show_hidden ?? '1',
            } : {}),
          },
        }
      );

      if (res.data.data?.length) {
        // Filtre de sécurité : ignore/rejette tout vol dont la compagnie
        // (marketing OU operating) a un code IATA interdit (ex: "ZZ").
        const filteredFlights = res.data.data.filter(
          (flight) =>
            !(flight.itineraries || []).some((itinerary) =>
              (itinerary.segments || []).some(
                (seg) =>
                  BANNED_AIRLINE_CODES.includes(seg?.carrierCode) ||
                  BANNED_AIRLINE_CODES.includes(seg?.operating?.carrierCode)
              )
            )
        );

        if (filteredFlights.length) {
          setFlights(filteredFlights);
          return filteredFlights;
        }
      }

      setErreur('Aucune offre trouvée.');
      return [];
    } catch (error) {
      console.error(error);
      setErreur("Erreur lors de la récupération des vols.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime) =>
    new Date(datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDate = (datetime) =>
    new Date(datetime).toLocaleDateString();

  const getAirportData = (iata) =>
    (airports || []).find((a) => a.iata_code === iata);

  const renderAirlineLogos = (carrierCodes) =>
    carrierCodes.map((iata, idx) => {
      const icao = getIcaoFromIata(iata);
      const logoPath = icao ? `./images/${icao}.png` : '/data/images/default.png';
      return (
        <img
          key={iata + '-' + idx}
          src={logoPath}
          alt={iata}
          title={iata}
          className="inline-block w-auto h-auto mr-2 border shadow"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    });



  const AirportWeather = ({ iata, datetime }) => {
    const airport = getAirportData(iata);
    const [weather, setWeather] = useState(null);

    useEffect(() => {
      if (airport?.latitude && airport?.longitude && datetime) {
        getWeatherForecast(airport.latitude, airport.longitude, datetime).then(setWeather);
      }
    }, [iata, datetime, airport?.latitude, airport?.longitude]);

    if (!weather) return null;

    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <CloudSun className="w-3 h-3 text-blue-400" />
        <span>{weatherIcons[weather.code] || "🌍"} {weather.temperature}°C</span>
      </span>
    );
  };



  const renderItinerary = (itinerary, type, label) => {
    const segments = itinerary.segments;
    const departure = segments[0];
    const arrival = segments[segments.length - 1];
    const stops = segments.length - 1;
    const airlineCodes = [...new Set(segments.map((s) => s.carrierCode))];
    const durationFormatted = itinerary.duration.replace("PT", "").toLowerCase();
    const depTime = formatTime(departure.departure.at);
    const arrTime = formatTime(arrival.arrival.at);
    const depDate = formatDate(departure.departure.at);

    const depMinutes = new Date(departure.departure.at).getHours() * 60 + new Date(departure.departure.at).getMinutes();
    const arrMinutes = new Date(arrival.arrival.at).getHours() * 60 + new Date(arrival.arrival.at).getMinutes();
    const travelMinutes = arrMinutes > depMinutes ? arrMinutes - depMinutes : arrMinutes + 1440 - depMinutes;
    const timelineWidth = Math.min(100, Math.max(30, (travelMinutes / 1440) * 100));

    const depAirport = getAirportData(departure.departure.iataCode);
    const arrAirport = getAirportData(arrival.arrival.iataCode);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`rounded-2xl p-3 sm:p-5 mb-3 sm:mb-4 border shadow-sm hover:shadow-md transition-shadow ${
          type === 'retour' 
            ? 'bg-gradient-to-br from-amber-50/80 to-white border-amber-200' 
            : 'bg-gradient-to-br from-blue-50/80 to-white border-blue-200'
        }`}
      >
        {/* En-tête : type de vol + date */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${type === 'retour' ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {type === 'retour' 
                ? <Globe className="text-amber-500 w-4 h-4" /> 
                : <Globe className="text-blue-500 w-4 h-4" />
              }
            </div>
            <span className={`text-sm font-bold ${type === 'retour' ? 'text-amber-700' : 'text-blue-700'}`}>
              {label || (type === 'retour' ? '🔄 Retour' : '🛫 Aller')}
            </span>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{depDate}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="font-medium">{durationFormatted}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span>{stops === 0 ? 'Direct' : `${stops} escale${stops > 1 ? 's' : ''}`}</span>
          </div>
        </div>

        {/* Timeline de vol moderne */}
        <div className="relative">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Départ */}
            <div className="flex flex-col items-center shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ring-blue-200">
                {depTime}
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-700 mt-1 uppercase">{departure.departure.iataCode}</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 truncate max-w-[60px] sm:max-w-[80px] text-center">
                {depAirport?.city || ''}
              </span>
            </div>

            {/* Barre de progression */}
            <div className="flex-1 relative h-0.5 bg-gray-200 rounded-full min-w-[40px]">
              <div 
                className={`absolute top-0 left-0 h-full rounded-full ${type === 'retour' ? 'bg-amber-400' : 'bg-blue-400'}`}
                style={{ width: `${timelineWidth}%` }}
              />
              {segments.slice(1).map((seg, idx) => (
                <div 
                  key={idx}
                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gray-300 border-2 border-white shadow"
                  style={{ left: `${(idx + 1) * (100 / segments.length)}%` }}
                  title={`Escale: ${seg.departure.iataCode}`}
                />
              ))}
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{ left: `${Math.min(timelineWidth, 90)}%` }}>
                <svg className={`w-4 h-4 ${type === 'retour' ? 'text-amber-500' : 'text-blue-500'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                </svg>
              </div>
            </div>

            {/* Arrivée */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ${
                type === 'retour' ? 'bg-amber-500 ring-amber-200' : 'bg-emerald-500 ring-emerald-200'
              }`}>
                {arrTime}
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-700 mt-1 uppercase">{arrival.arrival.iataCode}</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 truncate max-w-[60px] sm:max-w-[80px] text-center">
                {arrAirport?.city || ''}
              </span>
            </div>
          </div>
        </div>

        {/* Infos aéroports : météo + carte compacte */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* Aéroport départ */}
          <div className="bg-white/60 rounded-lg p-2 border border-gray-100">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase mb-1">
              <MapPin className="w-2.5 h-2.5" />
              {departure.departure.iataCode}
            </div>
            <p className="text-[10px] text-gray-600 leading-tight mb-1">{depAirport?.name || ''}</p>
            <div className="flex items-center gap-2">
              <AirportWeather iata={departure.departure.iataCode} datetime={departure.departure.at} />
            </div>
            {depAirport && (
              <div className="mt-1">
                <MapAeroports aeroports={depAirport} />
              </div>
            )}
          </div>

          {/* Aéroport arrivée */}
          <div className="bg-white/60 rounded-lg p-2 border border-gray-100">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase mb-1">
              <MapPin className="w-2.5 h-2.5" />
              {arrival.arrival.iataCode}
            </div>
            <p className="text-[10px] text-gray-600 leading-tight mb-1">{arrAirport?.name || ''}</p>
            <div className="flex items-center gap-2">
              <AirportWeather iata={arrival.arrival.iataCode} datetime={arrival.arrival.at} />
            </div>
            {arrAirport && (
              <div className="mt-1">
                <MapAeroports aeroports={arrAirport} />
              </div>
            )}
          </div>
        </div>

        {/* Compagnies et logos */}
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-gray-500">
          {airlineCodes.map((code, idx) => {
            const name = getCompagnyName(code);
            return (
              <span key={idx} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md">
                {renderAirlineLogos([code])}
                <span className="font-medium text-gray-700">{name || code}</span>
              </span>
            );
          })}
        </div>

        {/* Détails dépliants des segments */}
        {segments.length > 1 && (
          <details className="group mt-3 border-t border-gray-100 pt-2">
            <summary className="text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-800 transition flex items-center gap-1">
              <span>🛩️ Voir les {segments.length} segments</span>
              <svg className="w-3 h-3 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="mt-2 space-y-2">
              {segments.map((seg, idx) => (
                <div key={idx} className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-gray-700">{seg.carrierCode} {seg.number}</span>
                    <span className="text-gray-400">{aircraftTypes[seg.aircraft?.code] || seg.aircraft?.code || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="font-medium">{seg.departure.iataCode}</span>
                    <span>{formatTime(seg.departure.at)}</span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="font-medium">{seg.arrival.iataCode}</span>
                    <span>{formatTime(seg.arrival.at)}</span>
                    <span className="text-gray-400 ml-auto">{seg.duration.replace("PT", "").toLowerCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </motion.div>
    );
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      {/* Accès espace client (toujours visible) */}
      <a
        href={customer ? "/compte" : "/compte/connexion"}
        className="fixed top-4 right-4 z-40 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-full shadow-xl transition transform hover:scale-105"
      >
        <span className="text-lg">👤</span>
        <span>{customer ? `Bonjour, ${customer.firstName || customer.email}` : "Mon compte"}</span>
      </a>

      {/* Header / Hero */}
      <header className="relative w-full h-screen overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src="./videos/video1.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-black/10" />
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 flex flex-col justify-center items-center text-center h-full px-6"
        >
          <motion.h1
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-6xl md:text-8xl font-extrabold text-white drop-shadow-2xl tracking-wide"
          >
            Djater Voyage
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-6 text-lg md:text-2xl text-gray-200 max-w-2xl leading-relaxed"
          >
            ✈️ Partez à la découverte du monde avec{" "}
            <span className="text-yellow-400 font-semibold">style et sérénité</span>.
            <br /> Réservez et vivez vos plus belles aventures.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex flex-wrap gap-6 justify-center"
          >
            <a
              href="apropos"
              className="flex items-center gap-2 bg-white text-orange-600 hover:bg-orange-50 font-bold py-3 px-8 rounded-full shadow-xl transition transform hover:scale-105"
            >
              <Info className="w-5 h-5" />
              A propos
            </a>
            <a
              href="#contact"
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/40 text-white hover:bg-white/20 font-bold py-3 px-8 rounded-full transition transform hover:scale-105"
            >
              <Globe2 className="w-5 h-5" />
              Contactez-nous
            </a>
            <a
              href={customer ? "/compte" : "/compte/connexion"}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full shadow-xl transition transform hover:scale-105"
            >
              <span>👤</span>
              {customer ? "Mon tableau de bord" : "Mon compte / Réservations"}
            </a>
          </motion.div>
        </motion.div>
      </header>



      {selectedFlight && !reservationData && (
        <>
          {!showPassengerForm && !travelInfo && news.length === 0 && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
                <div className="mb-6">
                  <Loader2 className="mx-auto animate-spin text-blue-600 w-12 h-12 mb-4" />
                  <p className="text-gray-700 text-lg mb-2">Chargement des informations de voyage...</p>
                  <p className="text-gray-500 text-sm">Cela peut prendre quelques secondes</p>
                </div>
                <button
                  onClick={() => setShowPassengerForm(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition flex items-center justify-center gap-2"
                >
                  <span>Continuer sans attendre</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedFlight(null)}
                  className="mt-3 text-gray-500 hover:text-red-600 transition text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {!showPassengerForm && (travelInfo || news.length > 0) && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
              <div className="relative bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl p-8 animate-slideUp">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Informations voyage</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setNews([]); setTravelInfo(null); setShowPassengerForm(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-full shadow transition"
                    >
                      <span>Continuer la réservation</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setNews([]); setTravelInfo(null); setSelectedFlight(null); }}
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-red-500 hover:text-white text-gray-600 transition"
                      aria-label="Fermer"
                    >✕</button>
                  </div>
                </div>
                <div className="space-y-6">
                  {travelInfo && <div className="p-4 bg-gray-50 rounded-xl shadow-sm"><CountryInfo country={travelInfo} /></div>}
                  {news.length > 0 && <div className="p-4 bg-gray-50 rounded-xl shadow-sm"><NewsList news={news} /></div>}
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {showPassengerForm && (
              <PassengerForm
                flight={selectedFlight}
                onClose={() => { setShowPassengerForm(false); setSelectedFlight(null); }}
                onReservation={(amadeusData) => setReservationData(amadeusData)}
              />
            )}
          </AnimatePresence>
        </>
      )}



      {detailModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Informations détaillées</p>
                <h3 className="mt-1 text-xl font-bold text-slate-800">
                  {detailModal.type === 'details' ? 'Informations supplémentaires' : 'Détails'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDetailModal(null)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-lg text-slate-600 transition hover:bg-slate-200"
                aria-label="Fermer la fenêtre"
              >
                ×
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-5">
              {detailModal.type === 'details' ? (
                detailModal.loading ? (
                  <div className="flex min-h-[300px] flex-col items-center justify-center gap-5 py-8 text-center">
                    <motion.div
                      initial={{ opacity: 0.7, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25 }}
                      className="relative flex h-20 w-20 items-center justify-center"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                        className="absolute inset-0 rounded-full border-4 border-slate-200 border-t-blue-600 border-r-violet-400"
                      />
                      <motion.div
                        animate={{ rotate: -360 }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                        className="absolute inset-3 rounded-full border-4 border-slate-200 border-b-emerald-500 border-l-cyan-400"
                      />
                      <Loader2 className="relative h-7 w-7 text-blue-600" />
                    </motion.div>
                    <div>
                      <p className="text-lg font-semibold text-slate-800">Chargement des détails du vol...</p>
                      <p className="mt-1 text-sm text-slate-500">Récupération des informations complémentaires en cours.</p>
                    </div>
                  </div>
                ) : (
                <div className="space-y-5 text-sm text-slate-700">
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Offre complète</p>
                    <p className="mt-2 text-lg font-bold text-slate-800">
                      {detailModal.flight.duffel_fare_brand_name || detailModal.flight.fareBrand || 'Tarif standard'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">

                      <span className="rounded-full bg-white px-2 py-1 font-medium text-slate-700">
                        Voyage: {detailModal.flight.tripType || 'simple'}
                      </span>
                      <span className="rounded-full bg-white px-2 py-1 font-medium text-slate-700">
                        Cabine: {detailModal.flight.travelerPricings?.[0]?.fareDetailsBySegment?.[0]?.cabin || 'Standard'}
                      </span>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Remboursable</p>
                      <p className="mt-2 text-base font-semibold text-slate-800">
                        {formatDuffelCondition(detailModal.flight.duffel_conditions?.refund_before_departure)}
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modifiable</p>
                      <p className="mt-2 text-base font-semibold text-slate-800">
                        {formatDuffelCondition(detailModal.flight.duffel_conditions?.change_before_departure)}
                      </p>
                    </div>



                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Passagers</p>
                      <p className="mt-2 text-base font-semibold text-slate-800">
                        {(detailModal.flight.travelerPricings?.length || 1)} passager{(detailModal.flight.travelerPricings?.length || 1) > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  

                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-700">Cabine & confort</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {(() => {
                        const cabin = getCabinDetails(detailModal.flight);
                        const amenities = cabin?.amenities || {};
                        const seat = amenities?.seat || {};
                        const wifi = amenities?.wifi || {};
                        const power = amenities?.power || {};

                        return (
                          <>
                            <div className="rounded-lg border border-indigo-200 bg-white p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cabine</p>
                              <p className="mt-2 font-bold text-slate-800">{cabin?.marketing_name || cabin?.name || 'Standard'}</p>
                              <p className="mt-1 text-xs text-slate-600">Classe: {detailModal.flight?.cabin_class || cabin?.name || 'N/A'}</p>
                              <p className="mt-1 text-xs text-slate-600">Base tarif: {detailModal.flight?.fare_basis_code || 'N/A'}</p>
                            </div>
                            <div className="rounded-lg border border-indigo-200 bg-white p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Siège</p>
                              <p className="mt-2 text-xs text-slate-700">Pitch: {seat?.pitch || 'N/A'}</p>
                              <p className="mt-1 text-xs text-slate-700">Espace: {seat?.legroom || 'N/A'}</p>
                              <p className="mt-1 text-xs text-slate-700">Type: {seat?.type || 'N/A'}</p>
                            </div>
                            <div className="rounded-lg border border-indigo-200 bg-white p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wi‑Fi</p>
                              <p className="mt-2 text-xs text-slate-700">Disponible: {wifi?.available === true ? 'Oui' : 'Non'}</p>
                              <p className="mt-1 text-xs text-slate-700">Coût: {wifi?.cost || 'N/A'}</p>
                            </div>
                            <div className="rounded-lg border border-indigo-200 bg-white p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Alimentation</p>
                              <p className="mt-2 text-xs text-slate-700">Prise disponible: {power?.available === true ? 'Oui' : 'Non'}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Résumé du trajet</p>
                    <div className="space-y-3">
                      {(() => {
                        const slices = getOfferSlices(detailModal.flight);
                        const itineraryList = slices.length ? slices : (detailModal.flight.itineraries || []);

                        return itineraryList.map((itinerary, itineraryIndex) => {
                          const segments = itinerary?.segments || [];
                          const departure = segments[0];
                          const arrival = segments[segments.length - 1];
                          const stops = segments.length - 1;
                          const departureAirport = getAirportData(departure?.origin?.iata_code || departure?.departure?.iataCode || departure?.departure?.iataCode);
                          const arrivalAirport = getAirportData(arrival?.destination?.iata_code || arrival?.arrival?.iataCode || arrival?.arrival?.iataCode);
                          const departureAt = departure?.departing_at || departure?.departure?.at;
                          const arrivalAt = arrival?.arriving_at || arrival?.arrival?.at;

                          return (
                            <div key={itineraryIndex} className="rounded-lg border border-emerald-200 bg-white p-3">
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="font-bold text-emerald-800">
                                  {itineraryList.length > 1 ? (itineraryIndex === 0 ? 'Aller' : itineraryIndex === itineraryList.length - 1 ? 'Retour' : `Étape ${itineraryIndex + 1}`) : 'Trajet'}
                                </span>
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                  {stops === 0 ? 'Direct' : `${stops} escale${stops > 1 ? 's' : ''}`}
                                </span>
                              </div>
                              <div className="grid gap-2 sm:grid-cols-2 text-xs text-slate-600">
                                <div>
                                  <p className="font-semibold text-slate-700">Départ</p>
                                  <p>{departure?.origin?.iata_code || departure?.departure?.iataCode || 'N/A'} • {departureAirport?.city || departure?.origin?.city_name || 'Ville inconnue'}</p>
                                  <p>{departureAt ? new Date(departureAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-700">Arrivée</p>
                                  <p>{arrival?.destination?.iata_code || arrival?.arrival?.iataCode || 'N/A'} • {arrivalAirport?.city || arrival?.destination?.city_name || 'Ville inconnue'}</p>
                                  <p>{arrivalAt ? new Date(arrivalAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}</p>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-slate-600">
                                Durée: {itinerary?.duration?.replace('PT', '').toLowerCase() || 'Non précisée'}
                              </p>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">Segments détaillés</p>
                    <div className="space-y-3">
                      {(() => {
                        const slices = getOfferSlices(detailModal.flight);
                        const segments = slices.length ? slices.flatMap((slice, sliceIndex) => (slice?.segments || []).map((segment, segmentIndex) => ({ ...segment, __sliceIndex: sliceIndex, __segmentIndex: segmentIndex }))) : (detailModal.flight.itineraries || []).flatMap((itinerary, itineraryIndex) => (itinerary.segments || []).map((segment, segmentIndex) => ({ ...segment, __sliceIndex: itineraryIndex, __segmentIndex: segmentIndex })));

                        return segments.map((segment) => {
                          const departureAirport = getAirportData(segment.origin?.iata_code || segment.departure?.iataCode);
                          const arrivalAirport = getAirportData(segment.destination?.iata_code || segment.arrival?.iataCode);
                          const segmentLabel = segments.length > 1 ? (segment.__sliceIndex === 0 ? 'Aller' : segment.__sliceIndex === segments.length - 1 ? 'Retour' : `Étape ${segment.__sliceIndex + 1}`) : 'Trajet';
                          const aircraft = segment.aircraft || getFlightAircraft(detailModal.flight);
                          const departureAt = segment.departing_at || segment.departure?.at;
                          const arrivalAt = segment.arriving_at || segment.arrival?.at;
                          const carrierCode = segment.marketing_carrier?.iata_code || segment.carrierCode || 'N/A';
                          const flightNumber = segment.marketing_carrier_flight_number || segment.number || 'N/A';

                          return (
                            <div key={`${segment.__sliceIndex}-${segment.__segmentIndex}`} className="rounded-lg border border-slate-200 bg-white p-3">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-bold text-slate-800">{segmentLabel} • {carrierCode} {flightNumber}</span>
                                <span className="text-[11px] text-slate-500">{aircraft?.iata_code || aircraft?.code || aircraft?.iataCode || 'N/A'}</span>
                              </div>
                              <div className="mt-2 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                                <div>
                                  <p className="font-semibold text-slate-700">Départ</p>
                                  <p>{segment.origin?.iata_code || segment.departure?.iataCode} • {departureAirport?.city || segment.origin?.city_name || 'Ville inconnue'}</p>
                                  <p>{departureAt ? new Date(departureAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-700">Arrivée</p>
                                  <p>{segment.destination?.iata_code || segment.arrival?.iataCode} • {arrivalAirport?.city || segment.destination?.city_name || 'Ville inconnue'}</p>
                                  <p>{arrivalAt ? new Date(arrivalAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}</p>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-slate-600">Durée: {segment.duration?.replace('PT', '').toLowerCase() || 'Non précisée'}</p>
                              <p className="mt-1 text-xs text-slate-600">Terminal départ: {segment.origin_terminal || segment.departure?.terminal || detailModal.flight?.origin_terminal || 'Non communiqué'}</p>
                              <p className="mt-1 text-xs text-slate-600">Terminal arrivée: {segment.destination_terminal || segment.arrival?.terminal || detailModal.flight?.destination_terminal || 'Non communiqué'}</p>
                              <p className="mt-1 text-xs text-slate-600">Avion: {aircraft?.name || aircraft?.iata_code || aircraft?.iataCode || 'N/A'} ({aircraft?.iata_code || aircraft?.code || aircraft?.iataCode || 'N/A'})</p>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">Informations additionnelles</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg border border-cyan-200 bg-white p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terminal de départ</p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">{detailModal.flight?.origin_terminal || 'Non communiqué'}</p>
                      </div>
                      <div className="rounded-lg border border-cyan-200 bg-white p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terminal d’arrivée</p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">{detailModal.flight?.destination_terminal || 'Non communiqué'}</p>
                      </div>
                      <div className="rounded-lg border border-cyan-200 bg-white p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Avion</p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">{getFlightAircraft(detailModal.flight)?.name || 'N/A'}</p>
                        <p className="mt-1 text-xs text-slate-600">Code: {getFlightAircraft(detailModal.flight)?.iata_code || getFlightAircraft(detailModal.flight)?.code || getFlightAircraft(detailModal.flight)?.iataCode || 'N/A'}</p>
                      </div>
                      <div className="rounded-lg border border-cyan-200 bg-white p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Émissions</p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">{detailModal.flight?.total_emissions_kg || 'Non communiqué'}</p>
                        <p className="mt-1 text-xs text-slate-600">kg CO₂</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Bagages</p>
                    <div className="space-y-4">
                      {getDuffelBaggageDetails(detailModal.flight).length ? (
                        getDuffelBaggageDetails(detailModal.flight).map(({ itinerary, segment, label, quantity, weight, details }) => (
                          <div key={`${itinerary}-${segment}`} className="rounded-lg border border-amber-200 bg-white p-3">
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <span className="font-bold text-amber-800">{label} • Segment {segment}</span>
                              <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700">
                                {quantity > 0 ? `${quantity} bagage${quantity > 1 ? 's' : ''}` : 'Non communiqué'}
                              </span>
                            </div>
                            {weight ? (
                              <p className="mb-2"><span className="font-semibold text-slate-800">Poids maximum :</span> {weight}</p>
                            ) : (
                              <p className="mb-2 text-slate-600">Poids non communiqué par la compagnie.</p>
                            )}
                            <ul className="space-y-1.5">
                              {details.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-600">Aucune information de bagage détaillée n’est disponible pour ce vol.</p>
                      )}
                    </div>
                  </div>

                  {detailModal.flight.duffel_available_services?.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Services disponibles</p>
                      <ul className="space-y-1.5">
                        {detailModal.flight.duffel_available_services.map((service, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-500" />
                            <span>{service.name || service.type || service.id}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">Paiement & services</p>
                    
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date limite de paiement</p>
                      <p className="mt-2 text-sm text-slate-800">
                        {detailModal.flight.payment_requirements?.payment_required_by
                          ? new Date(detailModal.flight.payment_requirements.payment_required_by).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                          : 'Non communiqué'}
                      </p>
                      <p className="mt-2 text-xs text-slate-600">
                        Garantie de prix expirant le: {detailModal.flight.payment_requirements?.price_guarantee_expires_at
                          ? new Date(detailModal.flight.payment_requirements.price_guarantee_expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                          : 'Non communiqué'}
                      </p>
                    </div>
                  </div>
                </div>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}

      {reservationData ? (
        <ReservationSheet reservationData={reservationData} />
      ) : (
        <>
          <section id="search" className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 bg-white rounded-2xl shadow-xl mb-8 sm:mb-10 md:mb-12 border-2 border-blue-100">
            <FlightSearchForm onSearch={handleSearch} />
            <AnimatePresence>
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center mt-10">
                  <Loader2 className="mx-auto animate-spin text-blue-600 w-10 h-10" />
                  <p className="text-blue-600 mt-4 text-lg font-semibold">Recherche de vols en cours...</p>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {erreur && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="text-center mt-8 text-red-600 flex flex-col items-center">
                  <AlertTriangle className="w-8 h-8 mb-2" />
                  <p className="text-lg font-semibold">{erreur}</p>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {!loading && flights.length > 0 && (
                <>
                  <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-blue-700 mb-6 sm:mb-8"
                  >
                    ✈️ Résultats de recherche
                  </motion.h2>
                  <div className="mt-10 space-y-8">
                    {flights.map((flight, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.07 }}
                        className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                      >
                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                              {flight.travelerPricings[0]?.fareDetailsBySegment[0]?.cabin || 'Économique'}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="text-gray-500">
                              {flight.travelerPricings?.length || 1} passager{(flight.travelerPricings?.length || 1) > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        <div className="px-4 sm:px-6 py-3 sm:py-4">
                          {flight.itineraries.map((itin, iIdx) => {
                              const totalIt = flight.itineraries.length;
                              let type = 'aller';
                              let label = '🛫 Aller';
                              if (totalIt === 2 && iIdx === 1) { type = 'retour'; label = '🔄 Retour'; }
                              else if (totalIt > 2 && iIdx === totalIt - 1) { type = 'retour'; label = '🔄 Retour'; }
                              else if (totalIt > 2 && iIdx > 0) { type = 'aller'; label = `🚩 Étape ${iIdx + 1}`; }
                              if (flight.tripType === 'multiCity') {
                                type = 'aller';
                                label = `Etape ${iIdx + 1}`;
                              }
                              return <div key={iIdx}>{renderItinerary(itin, type, label)}</div>;
                            })}
                        </div>

                        <div className="px-4 sm:px-6 pt-3">
                          <button
                            type="button"
                            disabled={loadingDetails}
                            onClick={async () => {
                              if (flight.provider === 'duffel' && flight.duffel_offer_id && !flight._duffel_details_loaded) {
                                setLoadingDetails(true);
                                setDetailModal({ flight, type: 'details', loading: true });
                                try {
                                  const enriched = await enrichFlightWithDuffelOffer(flight);
                                  setFlights(prev => prev.map((f, i) => i === index ? { ...enriched, _duffel_details_loaded: true } : f));
                                  setDetailModal({ flight: { ...enriched, _duffel_details_loaded: true }, type: 'details', loading: false });
                                } catch (error) {
                                  console.error('Erreur lors du chargement du détail du vol:', error);
                                  setDetailModal({ flight, type: 'details', loading: false });
                                } finally {
                                  setLoadingDetails(false);
                                }
                                return;
                              }
                              setDetailModal({ flight, type: 'details', loading: false });
                            }}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-violet-700 shadow-sm transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-80"
                          >
                            {loadingDetails ? (
                              <>
                                <motion.span
                                  animate={{ rotate: 360 }}
                                  transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
                                  className="inline-flex"
                                >
                                  <Loader2 className="h-3.5 w-3.5" />
                                </motion.span>
                                Chargement...
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                                </svg>
                                Informations supplémentaires
                              </>
                            )}
                          </button>
                        </div>

                        {flight.provider === 'duffel' && (
                          <div className="px-4 sm:px-6 pb-3">
                            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:p-4 text-sm text-gray-700">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {flight.duffel_fare_brand_name && <span className="font-medium">{flight.duffel_fare_brand_name}</span>}
                              </div>
                              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <span>Remboursable avant départ : <strong>{formatDuffelCondition(flight.duffel_conditions?.refund_before_departure)}</strong></span>
                                <span>Modifiable avant départ : <strong>{formatDuffelCondition(flight.duffel_conditions?.change_before_departure)}</strong></span>
                              </div>
                              {flight.duffel_available_services?.length > 0 && (
                                <p className="mt-2 border-t border-blue-100 pt-2">
                                  <strong>Services disponibles :</strong>{" "}
                                  {flight.duffel_available_services.map((service) => service.name || service.type || service.id).filter(Boolean).join(", ")}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-50/50 via-white to-green-50/50 border-t border-gray-100">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex flex-col items-center sm:items-start">
                              <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider font-medium">Prix total TTC</span>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900">
                                  {new Intl.NumberFormat('fr-FR').format(getAdjustedPrice(flight))}
                                </span>
                                <span className="text-sm sm:text-base font-medium text-gray-500">{flight.price.currency}</span>
                              </div>
                              {getTravelerPriceBreakdown(flight).length > 1 && (
                                <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-1.5">
                                  {getTravelerPriceBreakdown(flight).map((item) => (
                                    <span
                                      key={item.type}
                                      className="rounded-full bg-white border border-green-100 px-2 py-1 text-[11px] font-medium text-gray-600"
                                    >
                                      {item.count} {getTravelerTypeLabel(item.type)} : {new Intl.NumberFormat('fr-FR').format(item.total)} {item.currency}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold py-2.5 sm:py-3 px-6 sm:px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm sm:text-base flex items-center justify-center gap-2"
                              onClick={() => setSelectedFlight({
                                ...flight,
                                adjustedPrice: getAdjustedPrice(flight),
                                itineraryType: getItineraryType(flight)
                              })}
                            >
                              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Réserver ce vol
                            </motion.button>
                          </div>
                        </div>

                        
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </AnimatePresence>
          </section>

          <PopularDestinations />
          <AutresDestinations />
          <Temoignages />
          <Footer />

          </>
      )}

      {/* Chat IA — toujours accessible sur la page d'accueil */}
      {!reservationData && !showPassengerForm && !selectedFlight && (
        <>
          <button
            onClick={() => setShowChat(!showChat)}
            aria-label={showChat ? "Fermer le chat IA" : "Ouvrir le chat IA"}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-4 pr-4 sm:pr-5 py-3 rounded-full shadow-xl shadow-orange-600/30 ring-2 ring-white/70 transition hover:scale-105 active:scale-95"
          >
            {showChat ? (
              <X className="w-5 h-5" />
            ) : (
              <>
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-semibold hidden sm:inline">
                  Assistant IA
                </span>
              </>
            )}
          </button>

          <AnimatePresence>
            {showChat && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.3 }}
                className="fixed bottom-6 right-6 z-50"
              >
                <Suspense fallback={null}>
                  <AITravelAssistant
                    onSearch={handleSearch}
                    onFlightSelect={(flight) => {
                      const adjustedPrice = getAdjustedPrice(flight);
                      const itineraryType = getItineraryType(flight);
                      setSelectedFlight({ ...flight, adjustedPrice, itineraryType });
                    }}
                    onOpenSearch={() => window.scrollTo({ top: document.getElementById('search').offsetTop, behavior: 'smooth' })}
                    onOpenReservation={() => setShowPassengerForm(true)}
                    voiceTrigger={voiceChatRequested}
                    onVoiceHandled={() => setVoiceChatRequested(false)}
                    onClose={() => setShowChat(false)}
                  />
                </Suspense>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

export default Home;
